<script setup lang="ts">
import merge from 'lodash-es/merge';

defineOptions({
    name: 'WidgetOpinionsSettings'
});

const { $t } = useLocalization();

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const defaults = ref<any>({
    description: '',
    bgColor: '#ffffff',
    image: null,
    buttonLink: '',
    buttonName: '',
    buttonBg: '#ffffff',
    quotes: []
});

const form = computed<FormType<any>>(() =>
{
    return props.modelValue;
});

function triggetModel(): void
{
    emit('update:modelValue', form.value);
}

function addQuote(): void
{
    const ids = form.value.settings.quotes.map((x: { id: any }) => x.id);

    const maxId = form.value.settings.quotes.length > 0 ? Math.max(...ids) : 1;

    form.value.settings.quotes.push({
        id: maxId + 1,
        quote: ''
    });
}

function removeQuote(quote: any): void
{
    form.value.settings.quotes = form.value.settings.quotes.filter((x: any) => x !== quote);
}

function setDefaultButtonBg(): void
{
    form.value.settings.buttonBg = '#ffffff';
}

function setDefaultBgColor(): void
{
    form.value.settings.bgColor = '#ffffff';
}

watch(() => form.value.settings, (value: any) =>
{
    triggetModel();
}, { deep: true });

onBeforeMount(async (): Promise<void> =>
{
    form.value.settings = merge(defaults.value, form.value.settings);

    if (!form.value.settings.quotes.length)
    {
        form.value.settings.quotes = [{
            id: 1,
            quote: ''
        }];
    }
});
</script>

<template>
    <div class="widget-opinions">
        <div class="widget-opinions__box widget-opinions--left">
            <div class="row">
                <IdeoFormGroup :label="$t('[[[Kolor tła]]]')" :invalid-feedback="form.$errors.first('settings.bgColor')" :state="form.$errors.state('settings.bgColor')" class="col-12 col-md-10">
                    <IdeoFormInput v-model="form.settings.bgColor" type="color" name="settings.bgColor" />
                </IdeoFormGroup>
                <div class="col-12 col-md-2 d-flex align-items-center justify-content-end pt-2">
                    <button type="button" class="btn btn-primary" @click="setDefaultBgColor">
                        {{ $t('[[[Resetuj]]]') }}
                    </button>
                </div>
            </div>
            <IdeoFormGroup :label="$t('[[[Link przycisku]]]')" :invalid-feedback="form.$errors.first('settings.buttonLink')" :state="form.$errors.state('settings.buttonLink')">
                <IdeoFormInput v-model="form.settings.buttonLink" type="text" name="buttonLink" />
            </IdeoFormGroup>
            <IdeoFormGroup :label="$t('[[[Nazwa przycisku]]]')" :invalid-feedback="form.$errors.first('settings.buttonName')" :state="form.$errors.state('settings.buttonName')">
                <IdeoFormInput v-model="form.settings.buttonName" type="text" name="buttonName" />
            </IdeoFormGroup>
            <div class="row">
                <IdeoFormGroup :label="$t('[[[Tło przycisku]]]')" :invalid-feedback="form.$errors.first('settings.buttonBg')" :state="form.$errors.state('settings.buttonBg')" class="col-12 col-md-10">
                    <IdeoFormInput v-model="form.settings.buttonBg" type="color" name="buttonBg" />
                </IdeoFormGroup>
                <div class="col-12 col-md-2 d-flex align-items-center justify-content-end pt-2">
                    <button type="button" class="btn btn-primary" @click="setDefaultButtonBg">
                        {{ $t('[[[Resetuj]]]') }}
                    </button>
                </div>
            </div>
            <IdeoFormGroup class="widget-opinions__box widget-opinions__box--right" :label="$t('[[[Opis]]]')" :invalid-feedback="form.$errors.first('settings.description')" :state="form.$errors.state('settings.description')">
                <RichTextEditor id="description" v-model="form.settings.description" name="settings.description" />
            </IdeoFormGroup>
        </div>
        <IdeoFormGroup class="widget-opinions__box widget-opinions__box--right" :label="$t('[[[Cytaty]]]')" :invalid-feedback="form.$errors.first('settings.quotes')" :state="form.$errors.state('settings.quotes')">
            <div v-for="(quote, i) in form.settings.quotes" :key="quote.id" class="col-12 quotes-item">
                <div class="mb-4">
                    <IdeoFormGroup :label="$t('[[[Logo]]]')" :invalid-feedback="form.$errors.first(`logo`)" :state="form.$errors.state(`logo`)">
                        <IdeoFilePicker v-model="form.settings.quotes[i].logo" media="images" />
                    </IdeoFormGroup>
                    <IdeoFormGroup :label="$t('[[[Podpis]]]')" :invalid-feedback="form.$errors.first(`signature`)" :state="form.$errors.state(`signature`)">
                        <IdeoFormInput v-model="form.settings.quotes[i].signature" type="text" name="signature" :maxlength="50" />
                    </IdeoFormGroup>
                    <IdeoFormGroup :label="$t('[[[Stanowisko]]]')" :invalid-feedback="form.$errors.first(`position`)" :state="form.$errors.state(`position`)">
                        <IdeoFormInput v-model="form.settings.quotes[i].position" type="text" name="position" :maxlength="100" />
                    </IdeoFormGroup>
                    <IdeoFormGroup :label="$t('[[[Cytat]]]')" :invalid-feedback="form.$errors.first(`quote`)" :state="form.$errors.state(`quote`)" required>
                        <IdeoFormTextarea v-model="form.settings.quotes[i].quote" name="quote" />
                    </IdeoFormGroup>
                    <div class="input-group-append">
                        <ActionButton
                            variant="danger"
                            class=""
                            icon="far fa-trash"
                            :text="$t('[[[Usuń]]]')"
                            :title="$t('[[[Usuń]]]')"
                            :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                            @click="removeQuote(quote)"
                        />
                    </div>
                </div>
            </div>
            <div class="col-12 mt-3">
                <button
                    type="button"
                    class="btn btn-primary"
                    icon="fas fa-check"
                    :disabled="form.settings.quotes.length >= 10"
                    @click="addQuote()"
                >
                    <i class="fas fa-plus me-2" />
                    {{ $t('[[[Dodaj nową opinie]]]') }}
                </button>
            </div>
        </IdeoFormGroup>
    </div>
</template>

<style lang="scss" scoped>
.widget-opinions {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 20px;
    &__box {
        &--left {
            order: 1;
        }
        &--right {
            order: 0;
        }
    }
    @media only screen and (min-width: 960px)
    {
        grid-template-columns: 1fr 1fr;

        &__box {
            &--left {
                order: 0;
            }
            &--right {
                order: 1;
            }
        }
    }
}
.quotes-item {
    grid-template-columns: 1fr;
    grid-gap: 16px;
    margin-bottom: 16px;
    background: #fafafb;
    padding-top: 8px;
    border: 1px solid #eaecee;
}
</style>
